import React, {useCallback} from 'react';
import PropTypes from 'prop-types';
import IssueCreatePanelWrapperContainer from './components/create-panel/IssueCreatePanelWrapperContainer';
import {
  pushpinExtensionType,
  documentForCreateType,
  sheetObjectType,
  initialFieldValuesType
} from './constants/proptype-validation';
import {useMappedInitialFieldValues} from './utils/issueCreate-helpers';
import BaseComponentContainer from './components/BaseComponentContainer';

const IssueCreatePanel = ({
  onSuccess,
  initialFieldValues,
  pushpinExtension,
  handleSave,
  isSaving,
  onCancel,
  targetUrn,
  document,
  currentSheet,
  availableStatuses,
  headerTitle,
  titleFieldLabel,
  titleFieldRequired
}) => {
  const handleOnSuccess = useCallback(({response}) => onSuccess({response}), [onSuccess]);
  const mappedInitialFieldValues = useMappedInitialFieldValues(initialFieldValues);
  return (
    <BaseComponentContainer>
      <IssueCreatePanelWrapperContainer
        pushpinExtension={pushpinExtension}
        handleSave={handleSave}
        isSaving={isSaving}
        onSuccess={handleOnSuccess}
        onCancel={onCancel}
        targetUrn={targetUrn}
        document={document}
        currentSheet={currentSheet}
        initialFieldValues={mappedInitialFieldValues}
        availableStatuses={availableStatuses}
        headerTitle={headerTitle}
        titleFieldLabel={titleFieldLabel}
        titleFieldRequired={titleFieldRequired}
      />
    </BaseComponentContainer>
  );
};

IssueCreatePanel.displayName = 'IssueCreatePanel';

IssueCreatePanel.propTypes = {
  /**
   * Callback when clicking save button. When this prop exists, issues-ui will not handle the saving process.
   */
  handleSave: PropTypes.func,
  /**
   * whether saving is currently in progress. Used when `handleSave` is used.
   */
  isSaving: PropTypes.bool,
  /**
   * Callback called after issue create success.
   * The handler is of the shape of "({response}) => *"
   */
  onSuccess: PropTypes.func,
  /**
   * Callback called when clicking cancel button
   */
  onCancel: PropTypes.func.isRequired,
  /**
   * pushpinExtension - used to handle pushpin interactions
   */
  pushpinExtension: pushpinExtensionType.isRequired,
  /**
   * Document's target URN
   */
  targetUrn: PropTypes.string.isRequired,
  /**
   * Document's data
   */
  document: documentForCreateType.isRequired,
  /**
   * Current sheet. Use when document has more than one sheet.
   */
  currentSheet: sheetObjectType,

  /**
   * An object containing the initial values for create form fields, in the form of {field: value} pairs.
   */
  initialFieldValues: initialFieldValuesType,

  /**
   * availableStatuses - An array of statuses to restrict.
   */
  availableStatuses: PropTypes.array,

  /**
   * headerTitle - Override default header title.
   */
  headerTitle: PropTypes.string,

  /**
  * titleFieldLabel - Override default title field label.
  */
  titleFieldLabel: PropTypes.string,

  /**
  * titleFieldRequired - Override default title field required validation.
  */
  titleFieldRequired: PropTypes.bool
};

export default IssueCreatePanel;
